import { useDraggable } from "@dnd-kit/core";
import {
  GripVertical,
  History,
  Eye,
  ImageOff,
  Bell,
  CheckCircle2,
  Info,
  Copy,
  Loader2,
  Lock,
} from "lucide-react";
import { useState, useCallback } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime, formatTimeAgo } from "@/shared/utils/dateTimeHelpers";

import type { Payment } from "../types/payments.types";
import { canReleasePayment } from "../utils/canReleasePayment";
import { formatPaymentAmount } from "../utils/formatPaymentAmount";

type PaymentCardProps = {
  payment: Payment;
  hidden?: boolean;
  onCancel?: (id: string) => void;
  onTake?: (id: string) => void;
  /** Opens the release confirmation. Whether it is a force release is decided by the board, not the card. */
  onRelease?: (id: string) => void;
  currentUserId?: string;
  canForceRelease?: boolean;
  onViewHistory?: (id: string) => void;
  onViewPaymentInfo?: (id: string) => void;
  onNotifyClient?: (id: string) => void;
  onView?: (id: string, imageUrls?: string[] | null) => void;
  onTakerReminder?: (id: string) => void | Promise<void>;
};

const getStatusConfig = (
  t: (k: keyof import("@/shared/lang/interfaces/payments").PaymentsTranslations) => string,
) =>
  ({
    pending: {
      borderClass: "border-l-blue-300 dark:border-l-blue-400",
      badgeClass: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400",
      label: t("card_status_pending"),
    },
    in_progress: {
      borderClass: "border-l-amber-300 dark:border-l-amber-400",
      badgeClass: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400",
      label: t("card_status_in_progress"),
    },
    paid: {
      borderClass: "border-l-green-300 dark:border-l-green-400",
      badgeClass: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400",
      label: t("card_status_paid"),
    },
  }) as const;

export const PaymentCard = ({
  payment,
  hidden = false,
  onCancel,
  onTake,
  onRelease,
  currentUserId,
  canForceRelease = false,
  onViewHistory,
  onViewPaymentInfo,
  onNotifyClient,
  onView,
  onTakerReminder,
}: PaymentCardProps) => {
  const { t, activeLanguage } = useTranslations("payments");
  const { t: tConfirmations } = useTranslations("confirmations");
  const statusConfig = getStatusConfig(t);
  const config = statusConfig[payment.status];
  const isDraggable = payment.status === "pending" || payment.status === "in_progress";
  const isPending = payment.status === "pending";
  const isInProgress = payment.status === "in_progress";
  const isPaid = payment.status === "paid";
  const createdAt = payment.createdAt ?? payment.date;
  const updatedAt = payment.updatedAt ?? payment.date;
  const attachmentUrls = payment.attachmentUrls ?? [];
  const hasAttachments = attachmentUrls.length > 0;
  const [failedUrlIndices, setFailedUrlIndices] = useState<Set<number>>(new Set());
  const [urlHasCopied, setUrlHasCopied] = useState(false);
  const [isTakerReminderSending, setIsTakerReminderSending] = useState(false);

  // Who holds the payment, and for how long. On an IN_PROGRESS card that is the live lock; on a PENDING one
  // it is a leftover from the old non-atomic take, and surfacing it is what tells the user why "Tomar" keeps
  // failing. The age is what an admin needs to judge whether taking the lock away is warranted.
  const takenByMe = Boolean(payment.takenById && payment.takenById === currentUserId);
  const takenByOther = Boolean(payment.takenById && payment.takenById !== currentUserId);
  const canRelease = Boolean(onRelease) && canReleasePayment(payment, currentUserId, canForceRelease);
  const takenAgo = payment.takenAt ? formatTimeAgo(payment.takenAt, { locale: activeLanguage }) : "";

  const handleAttachmentError = (index: number) => {
    setFailedUrlIndices((prev) => new Set(prev).add(index));
  };
  const handleCopyUrlHas = useCallback(() => {
    if (payment.urlHas) {
      void navigator.clipboard.writeText(payment.urlHas);
      setUrlHasCopied(true);
      setTimeout(() => setUrlHasCopied(false), 1500);
    }
  }, [payment.urlHas]);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: payment.id,
    disabled: !isDraggable,
  });

  const takerReminderButton = onTakerReminder ? (
    <button
      type="button"
      onClick={async () => {
        setIsTakerReminderSending(true);
        try {
          await onTakerReminder(payment.id);
        } finally {
          setIsTakerReminderSending(false);
        }
      }}
      disabled={isTakerReminderSending}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-sky-800 dark:text-sky-100",
        "bg-sky-100 hover:bg-sky-200/90 dark:bg-sky-900/40 dark:hover:bg-sky-900/60",
        "shadow-sm transition-colors border-0",
        "disabled:opacity-60 disabled:pointer-events-none self-start",
      )}
      aria-label={tConfirmations("card_button_taker_reminder")}
    >
      <span
        className="inline-flex shrink-0 rounded-full border-2 border-yellow-500 p-1.5 text-yellow-600 dark:border-yellow-400 dark:text-yellow-300"
        aria-hidden
      >
        {isTakerReminderSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
      </span>
      {tConfirmations("card_button_taker_reminder")}
    </button>
  ) : null;

  // Rendered from both the Pendientes and En proceso footers. Built once so the two can never
  // drift in label, styling or hint the way they did before.
  const releaseButton = canRelease ? (
    <button
      type="button"
      onClick={() => onRelease?.(payment.id)}
      className="text-xs font-semibold text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 transition-colors uppercase tracking-tight"
      title={t("card_release_title")}
    >
      {takenByMe ? t("card_release") : t("card_release_admin")}
    </button>
  ) : null;

  const cardContent = (
    <>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-start gap-2 min-w-0">
          {isDraggable && (
            <span className="shrink-0 mt-0.5 text-slate-400 dark:text-slate-500 touch-none" aria-hidden>
              <GripVertical className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0 flex flex-col gap-2">
            <div>
              <h4 className="font-bold text-slate-900 dark:text-white truncate">{payment.name}</h4>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                {payment.currency} {formatPaymentAmount(payment.amount)}
              </p>
            </div>
            {takerReminderButton}
          </div>
        </div>
        <span
          className={cn(
            "shrink-0 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-tight",
            config.badgeClass,
          )}
        >
          {config.label}
        </span>
      </div>

      <div className="space-y-1.5 text-sm text-slate-500 dark:text-slate-400 mb-6">
        {payment.referenceCode && (
          <div className="flex justify-end">
            <span className="text-xs font-semibold text-blue-600/90 dark:text-blue-400/90 tracking-tight">
              {payment.referenceCode}
            </span>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <span>{t("card_beneficiary")}</span>
          <span className="font-medium text-slate-700 dark:text-slate-300 truncate">{payment.payer}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span>{t("card_platform")}</span>
          <span className="font-medium text-slate-700 dark:text-slate-300 truncate">{payment.platform}</span>
        </div>
        {isPending ? (
          <div className="flex justify-between gap-2">
            <span>{t("card_creation_date")}</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {formatDateTime(createdAt)}
            </span>
          </div>
        ) : (
          <>
            <div className="flex justify-between gap-2">
              <span>{t("card_creation_date")}</span>
              <span className="font-medium text-slate-700 dark:text-slate-300">
                {formatDateTime(createdAt)}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span>{t("card_update_date")}</span>
              <span className="font-medium text-slate-700 dark:text-slate-300">
                {formatDateTime(updatedAt)}
              </span>
            </div>
          </>
        )}
        {payment.note && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[11px] opacity-75 block">
              {t("card_note")} {payment.note}
            </span>
          </div>
        )}
        {payment.createdBy && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[11px] opacity-75">
              {t("card_created_by")} {payment.createdBy}
            </span>
          </div>
        )}
        {(isPending || isInProgress) && payment.takenById && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 px-2 py-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
              <Lock className="h-3 w-3 shrink-0" aria-hidden />
              {takenByMe ? t("card_taken_by_you") : t("card_taken_by", { name: payment.takenByName ?? "" })}
              {takenAgo && (
                <>
                  <span aria-hidden>·</span>
                  <span className="font-normal">{takenAgo}</span>
                </>
              )}
            </span>
          </div>
        )}
        {/*
          Paid only: on an IN_PROGRESS card this is the taker, already shown by the lock badge above with the
          holder's name and how long they have held it. Here it means who settled the payment.
        */}
        {isPaid && payment.updatedByEmail && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[11px] opacity-75">
              {t("card_updated_by")} {payment.updatedByEmail}
            </span>
          </div>
        )}
        {isPaid && onNotifyClient && (
          <div className="mb-4">
            <button
              type="button"
              onClick={() => onNotifyClient(payment.id)}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-violet-500 hover:bg-violet-600 active:bg-violet-700 text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all"
            >
              <Bell className="h-5 w-5 shrink-0" />
              {t("card_notify_client")}
            </button>
          </div>
        )}
        {isPaid && payment.urlHas && (
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
            {payment.platform && (
              <div>
                <span className="text-[11px] opacity-75">{t("card_network_label")}: </span>
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  {payment.platform}
                </span>
              </div>
            )}
            <div>
              <span className="text-[11px] opacity-75 block mb-0.5">{t("card_url_has_label")}</span>
              <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 overflow-hidden">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300 break-all min-w-0 flex-1 py-2 px-3">
                  {payment.urlHas}
                </span>
                <button
                  type="button"
                  onClick={handleCopyUrlHas}
                  className="shrink-0 flex items-center gap-1 px-2.5 py-2 text-[10px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 border-l border-slate-200 dark:border-slate-700 transition-colors"
                  title={t("card_copy_title")}
                >
                  {urlHasCopied ? (
                    <>{t("card_copied")}</>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      {t("card_copy")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-4">
        {isPending ? (
          <>
            <div className="flex space-x-3">
              {onCancel && (
                <button
                  type="button"
                  onClick={() => onCancel(payment.id)}
                  className="text-xs font-semibold text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors uppercase tracking-tight"
                >
                  {t("card_cancel")}
                </button>
              )}
              {onTake && !takenByOther && (
                <button
                  type="button"
                  onClick={() => onTake(payment.id)}
                  className="text-xs font-semibold text-green-500 hover:text-green-600 dark:hover:text-green-400 transition-colors uppercase tracking-tight"
                >
                  {takenByMe ? t("card_take_continue") : t("card_take")}
                </button>
              )}
              {releaseButton}
            </div>
            {onViewHistory && (
              <button
                type="button"
                onClick={() => onViewHistory(payment.id)}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors uppercase tracking-tight"
              >
                {t("card_view_history")}
              </button>
            )}
          </>
        ) : isPaid ? (
          <>
            {payment.verifiedByAdmin ? (
              <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-xs font-semibold uppercase tracking-tight">{t("card_verified")}</span>
              </div>
            ) : (
              <div />
            )}
            {onViewHistory && (
              <button
                type="button"
                onClick={() => onViewHistory(payment.id)}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors uppercase tracking-tight flex items-center gap-1"
              >
                <History className="h-3.5 w-3.5" />
                {t("card_view_history")}
              </button>
            )}
          </>
        ) : (
          <>
            {onCancel || canRelease ? (
              <div className="flex items-center space-x-3">
                {onCancel && (
                  <button
                    type="button"
                    onClick={() => onCancel(payment.id)}
                    className="text-xs font-semibold text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors uppercase tracking-tight"
                  >
                    {t("card_cancel")}
                  </button>
                )}
                {releaseButton}
              </div>
            ) : (
              <div />
            )}
            <div className="flex items-center space-x-3">
              {onViewPaymentInfo && (
                <button
                  type="button"
                  onClick={() => onViewPaymentInfo(payment.id)}
                  className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors uppercase tracking-tight flex items-center gap-1"
                >
                  <Info className="h-3.5 w-3.5" />
                  {t("card_payment_info")}
                </button>
              )}
              {onViewHistory && (
                <button
                  type="button"
                  onClick={() => onViewHistory(payment.id)}
                  className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors uppercase tracking-tight flex items-center gap-1"
                >
                  <History className="h-3.5 w-3.5" />
                  {t("card_view_history")}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {isPaid && hasAttachments && onView && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-2">
            {attachmentUrls.length > 1
              ? t("card_receipts_count") + ` (${attachmentUrls.length})`
              : t("card_receipts") + ` (${attachmentUrls.length})`}
          </p>
          <div
            className={cn(
              "grid gap-2 w-full rounded-lg overflow-hidden",
              attachmentUrls.length === 1
                ? "grid-cols-1 min-h-[120px]"
                : attachmentUrls.length === 2
                  ? "grid-cols-2"
                  : "grid-cols-3",
            )}
          >
            {attachmentUrls.map((url, index) => (
              <div
                key={`${url}-${index}`}
                className="relative rounded-lg overflow-hidden min-h-[100px] bg-slate-100 dark:bg-zinc-800 aspect-4/3"
              >
                {!failedUrlIndices.has(index) ? (
                  <img
                    src={url}
                    alt={t("card_alt_receipt_n", { n: String(index + 1) })}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={() => handleAttachmentError(index)}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-500 dark:text-slate-400 p-2">
                    <ImageOff className="h-8 w-8 shrink-0 opacity-60" />
                    <span className="text-[10px] text-center">{t("card_error_load")}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <button
              type="button"
              onClick={() => onView(payment.id, attachmentUrls.length > 0 ? attachmentUrls : null)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-medium text-sm hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <Eye className="h-4 w-4 shrink-0" />
              {t("card_view")}
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (isDraggable) {
    return (
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        data-payment-id={payment.id}
        className={cn(
          "bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-5 rounded-xl shadow-sm transition-all duration-200 border-l-4 cursor-grab active:cursor-grabbing",
          config.borderClass,
          hidden && "opacity-0 pointer-events-none transition-opacity duration-200",
          isDragging &&
            !hidden &&
            "opacity-40 scale-[0.98] border-2 border-dashed border-blue-300/50 dark:border-blue-400/50",
        )}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div
      data-payment-id={payment.id}
      className={cn(
        "bg-white dark:bg-zinc-900 border border-slate-200 dark:border-slate-800 p-5 rounded-xl shadow-sm border-l-4 transition-opacity duration-200",
        config.borderClass,
        hidden && "opacity-0 pointer-events-none",
      )}
    >
      {cardContent}
    </div>
  );
};
